import notifier from '@albert-io/notifier';
import {NestedMarkdown} from 'generic/MarkdownRendererV2/NestedMarkdown.react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import AudioControls from './AudioControls.react';
import {useAudioEvents, useTrackEvents} from './AudioPlayer.utils';
import {useAudioPlayer} from './AudioPlayerProvider.react';
import AudioTrack from './AudioTrack.react';
import './audio-player.scss';

const playbackRates = [0.5, 1, 2, 3, 4];

interface AudioPlayerProps {
  src: string;
  caption?: string;
  onError?: (hasError: boolean, errorMessage: string) => void;
}

/**
 * Report audio play promise rejection to Honeybadger
 */
function reportPlayError(error: Error, audio: HTMLAudioElement, src: string, action: string) {
  notifier.notify(error, {
    tags: 'AudioPlayer',
    context: {
      errorType: `PlayPromiseRejection-${action}`,
      errorName: error.name,
      errorMessage: error.message,
      audioSrc: src,
      userAgent: navigator.userAgent,
      readyState: audio.readyState,
      networkState: audio.networkState,
      paused: audio.paused
    }
  });
}

export const AudioPlayer = ({src, caption, onError}: AudioPlayerProps) => {
  const {players, registerPlayer, updatePlayerState} = useAudioPlayer();
  const playerState = players[src] || {};
  const {playbackRate, timestamp, duration, isPlaying, pauseFired} = playerState;
  const [isRegistered, setIsRegistered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackRef = useRef<HTMLInputElement>(null);

  useAudioEvents(audioRef, src, updatePlayerState);
  useTrackEvents(trackRef);

  useEffect(() => {
    registerPlayer(src);
  }, [src, registerPlayer]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && Object.keys(playerState).length > 0 && !isRegistered) {
      audio.currentTime = timestamp;
      audio.playbackRate = playbackRate;
      setIsRegistered(true);
    }
  }, [playerState]);

  useEffect(() => {
    if (audioRef.current && pauseFired) {
      audioRef.current.pause();
      updatePlayerState(src, {pauseFired: false});
    }
  }, [pauseFired]);

  const handleAudioError = useCallback(
    (event: React.SyntheticEvent<HTMLAudioElement>) => {
      const audio = event.currentTarget;
      const error = audio.error;
      let message = 'Unable to play audio file';

      if (error) {
        switch (error.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            message = 'Audio playback was aborted';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            message = 'Audio loading failed due to network error';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            message = 'Audio file format is not supported or corrupted';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            message = 'Audio file format is not supported';
            break;
          default:
            message = 'Unable to play audio file';
        }
      }

      setHasError(true);
      setErrorMessage(message);

      if (onError) {
        onError(true, message);
      }

      notifier.notify(new Error('Audio playback error'), {
        message: 'Audio player failed to load or play file',
        tags: 'AudioPlayer',
        context: {
          errorCode: error?.code,
          errorMessage: error?.message,
          audioSrc: src,
          userAgent: navigator.userAgent
        }
      });
    },
    [src, onError]
  );

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    const currPlayState = players[src].isPlaying;

    if (currPlayState) {
      // Pausing is synchronous and reliable
      audio.pause();
      updatePlayerState(src, {isPlaying: false});
    } else {
      // Optimistically update UI for immediate feedback
      updatePlayerState(src, {isPlaying: true});

      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // ROLLBACK: Revert state on failure
          updatePlayerState(src, {isPlaying: false});
          reportPlayError(error, audio, src, 'togglePlay');
        });
      }
    }
  };

  const seekAudio = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const seekTime = parseFloat(event.target.value);
      audioRef.current.currentTime = seekTime;
      updatePlayerState(src, {timestamp: seekTime});
    }
  };

  const restartAudio = () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    audio.currentTime = 0;
    if (!isPlaying) {
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            updatePlayerState(src, {isPlaying: true});
          })
          .catch((error) => {
            reportPlayError(error, audio, src, 'restart');
            updatePlayerState(src, {isPlaying: false});
          });
      } else {
        // Fallback for browsers where play() does not return a Promise
        updatePlayerState(src, {isPlaying: true});
      }
    }
  };

  const changeRate = () => {
    if (audioRef.current) {
      const currentIndex = playbackRates.indexOf(playbackRate);
      const nextIndex = (currentIndex + 1) % playbackRates.length;
      const nextSpeed = playbackRates[nextIndex];
      audioRef.current.playbackRate = nextSpeed;
      updatePlayerState(src, {playbackRate: nextSpeed});
    }
  };

  return (
    <div className='audio-player__container background-subtle'>
      <audio
        ref={audioRef}
        src={src}
        controls={false}
        preload='metadata'
        onError={handleAudioError}
      >
        <track kind='captions' />
      </audio>
      {hasError ? (
        <div className='audio-player__error content-negative p-3'>
          <i className='fa fa-exclamation-triangle mr-2' />
          {errorMessage}
        </div>
      ) : (
        <>
          <div className='audio-player__title-track'>
            {caption && <NestedMarkdown className='audio-player__title' text={caption} />}
            <AudioTrack
              duration={duration}
              timestamp={timestamp}
              seekAudio={seekAudio}
              isPlaying={isPlaying}
              trackRef={trackRef}
            />
          </div>
          <AudioControls
            isPlaying={isPlaying}
            playbackRate={playbackRate}
            togglePlayPause={togglePlayPause}
            restartAudio={restartAudio}
            changeRate={changeRate}
          />
        </>
      )}
    </div>
  );
};
